// src/modules/auth/contexts/auth-plus.context.tsx

import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { AuthContextType } from "../interfaces/auth-context-type.interface";
import { AuthenticationEntity } from "../entities/auth.entity";
import { AuthenticationService } from "../services/auth.service";
import { ErrorEntity } from "@modules/shared/entities/errors/error.entity";
import { authSyncService } from "../services/auth-sync.service";
import { useLoading } from "@modules/shared/helpers/loading-provider.helper";

export const AuthContext = createContext<AuthContextType>({
  auth: null,
  setAuth: () => {},
  isAuthenticated: false,
  logout: () => {},
  loading: true,
});

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [auth, setAuth] = useState<AuthenticationEntity | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoading();

  const logout = useCallback(() => {
    localStorage.removeItem("refresh_token");
    sessionStorage.removeItem("refresh_token");
    localStorage.removeItem("access_token");
    sessionStorage.removeItem("access_token");
    setAuth(null);
    navigate("/login", { replace: true });
  }, [navigate]);

  useEffect(() => {
    const checkAuth = async () => {
      startLoading("auth-check");
      const refreshToken =
        localStorage.getItem("refresh_token") ||
        sessionStorage.getItem("refresh_token");
      const accessToken =
        localStorage.getItem("access_token") ||
        sessionStorage.getItem("access_token");

      // Không có refresh token => logout
      if (!refreshToken) {
        setAuth(null);
        setLoading(false);
        stopLoading();
        return;
      }

      // Nếu có access token, khôi phục auth mà không gọi refresh
      if (accessToken) {
        const authEntity = new AuthenticationEntity(accessToken, refreshToken);
        setAuth(authEntity);
        setLoading(false);
        stopLoading();
        return;
      }

      // Không có access token (có thể đã hết hạn), gọi refresh
      try {
        const result = await new AuthenticationService().refresh(refreshToken);

        if (result instanceof ErrorEntity) {
          localStorage.removeItem("refresh_token");
          sessionStorage.removeItem("refresh_token");
          setAuth(null);
        } else {
          const newAuth = result as AuthenticationEntity;
          const newAccessToken = newAuth.getAccessToken();
          const newRefreshToken = newAuth.getRefreshToken();

          setAuth(newAuth);

          // Lưu cả access token và refresh token mới
          sessionStorage.setItem("access_token", newAccessToken);
          localStorage.setItem("access_token", newAccessToken);

          if (newRefreshToken !== refreshToken) {
            sessionStorage.setItem("refresh_token", newRefreshToken);
            localStorage.setItem("refresh_token", newRefreshToken);
          }
        }
      } catch {
        localStorage.removeItem("refresh_token");
        sessionStorage.removeItem("refresh_token");
        setAuth(null);
      } finally {
        setLoading(false);
        stopLoading();
      }
    };

    checkAuth();
  }, [startLoading, stopLoading]);

  // Đồng bộ multi‑tab
  useEffect(() => {
    authSyncService.onTokenRefreshed = ({ accessToken, refreshToken }) => {
      const newAuth = new AuthenticationEntity(accessToken, refreshToken);
      setAuth(newAuth);
      localStorage.setItem("refresh_token", refreshToken);
      sessionStorage.setItem("refresh_token", refreshToken);
      localStorage.setItem("access_token", accessToken);
      sessionStorage.setItem("access_token", accessToken);
    };
    return () => {
      authSyncService.onTokenRefreshed = undefined;
    };
  }, []);

  const value = useMemo(
    () => ({
      auth,
      setAuth,
      isAuthenticated: !!auth,
      logout,
      loading,
    }),
    [auth, logout, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
